import { Injectable }     from '@angular/core';
import {Observable} from 'rxjs/Observable';
import {BehaviorSubject} from 'rxjs/Rx';
import { HttpClient } from '@angular/common/http';

import {SecretService} from './secret';
import {RoutesService} from './routes';
import {TrackerService} from './tracker';
import {METHODS} from './utils';

declare let window: any;
declare let $: any;

const OAUTH_TOKEN_NAMES = {
  'slack.com': 'token',
}

const X_VALUES = [
  'dynamicEnum',
  'dynamicValue',
  'hidden',
  'global',
  'consoleDefault',
  'inputType',
  'group',
  'enumLabels',
  'showCondition',
  'parameterGroups',
];
const getSeparatorFromFormat = function(format) {
  if (format === 'multi') return;
  if (!format || format === 'csv') {
    return ',';
  } else if (format === 'tsv') {
    return '\t';
  } else if (format === 'ssv') {
    return ' ';
  } else if (format === 'pipes') {
    return '|';
  }
}

interface APICall {
  id:string;
  method:string;
  path:string;
  title:string;
  operation:any;
}

@Injectable()
export class OpenAPIService {
  currentName:string;
  specSubject:BehaviorSubject<any> = new BehaviorSubject(null);
  spec:Observable<any>;
  versions:any[] = [];
  currentVersion:any;
  targetVersion:string;
  parsed:any;
  original:any;
  apiCalls:APICall[];
  globalParameters:any = {};
  loading:boolean;
  subscriptions:any[] = [];

  constructor(private http:HttpClient,
              private routes:RoutesService,
              private tracker:TrackerService,
              public secrets:SecretService) {
    window.lucybot.openapiService = this;
    this.spec = this.specSubject.asObservable();
    this.subscriptions.push(this.spec.subscribe(spec => {
      if (!spec) return;
      this.tracker.track('openapi', {
        host: spec.host,
        version: spec.info.version,
        title: spec.info.title,
      })
    }));

    this.subscriptions.push(this.secrets.onChange.subscribe(newSecrets => {
      for (let key in newSecrets) this.globalParameters[key] = newSecrets[key];
    }));
    for (let key in this.secrets.secrets) this.globalParameters[key] = this.secrets.secrets[key];

    this.subscriptions.push(this.routes.currentRoute.subscribe(route => {
      if (route) this.retrieveSpec(route.openapi);
      for (let key in this.globalParameters) delete this.globalParameters[key];
    }));
    this.subscriptions.push(this.routes.router.routerState.root.children[0].queryParams.subscribe(qs => {
      this.targetVersion = qs['apiVersion'];
      if (this.currentVersion && this.currentVersion.label !== this.targetVersion) {
        let newVersion = this.versions.filter(v => v.label === this.targetVersion)[0];
        if (newVersion) this.retrieveVersion(newVersion);
      }
    }));
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  retrieveSpec(name) {
    if (name === this.currentName) return;
    if (this.currentName) {
      this.secrets.clearSecrets();
    }
    this.currentName = name;
    if (!name) {
      this.currentName = null;
      this.parsed = this.original = null;
      this.specSubject.next(null);
      return;
    }
    this.versions = window.config.openapi ? window.config.openapi[name] : [];
    if (window.lucybot.openapi[name] && !this.targetVersion) {
      this.original = window.lucybot.openapi[name];
      this.currentVersion = this.versions[0];
      this.initialize();
      this.specSubject.next(this.parsed);
    } else {
      let version = this.versions[0];
      if (this.targetVersion) {
        version = this.versions.filter(v => v.label === this.targetVersion)[0] || version;
      }
      this.retrieveVersion(version);
    }
  }

  retrieveVersion(version) {
    let name = this.currentName;
    this.currentVersion = version;
    this.loading = true;
    this.original = this.parsed = null;
    this.specSubject.next(null);
    if (window.config.openapi[name][0] !== version) {
      this.routes.addQueryParams({apiVersion: version.label});
    } else {
      this.routes.addQueryParams({apiVersion: null});
    }
    let specURL = 'openapi/' + name + '.' + version.label.replace(/\W+/g, '_') + '.openapi.json';
    this.http.get(specURL).toPromise()
      .then(spec => {
        this.original = spec;
      })
      .then(() => this.initialize())
      .then(spec => this.specSubject.next(this.parsed))
      .then(() => this.loading = false);
  }

  getAPICall(title) {
    return this.apiCalls.filter(a => a.title === title)[0];
  }

  resolveAPICall(info) {
    return this.apiCalls.filter(apiCall => {
      if (info.title === apiCall.title) return true;
      if (info.method === apiCall.method && info.path === apiCall.path) return true;
      return false;
    })[0];
  }

  operationMatchesQuery(op, query='') {
    let searchText = op.method + ' ' + op.path + ' ' + (op.operation.operationId || '');
    return searchText.indexOf(query) !== -1;
  }

  getAnswers(defaults:any={}) {
    let answers = {};
    for (let key in this.secrets.secrets) {
      answers[key] = this.secrets.secrets[key];
    }
    for (let key in this.globalParameters) {
      answers[key] = this.globalParameters[key];
    }
    for (let key in defaults) {
      answers[key] = defaults[key];
    }
    return answers;
  }

  openAPIRequest(opts) {
    var req:any = {};
    var op      = this.parsed.paths[opts.path][opts.method];
    if (!op) {
      console.log('Operation not found', opts.path, opts.method);
      return;
    }
    let answers = this.getAnswers(opts.answers);
    if (opts.embed) {
      for (var key in window.config.embedParameters || {}) {
        if (answers[key] === undefined) answers[key] = window.config.embedParameters[key];
      }
    }

    req.protocol = this.parsed.schemes.indexOf('https') === -1 ? this.parsed.schemes[0] : 'https';
    req.domain   = this.parsed.host;
    req.method   = opts.method;
    var basePath = this.parsed.basePath;
    if (basePath.lastIndexOf('/') === basePath.length - 1) basePath = basePath.substring(0, basePath.length - 1);
    req.path = basePath + opts.path;
    var consumes = op.consumes || ['application/json'];
    var contentType = '';
    var useTraditional = false;

    const addParam = (parameter, answer) => {
      if (parameter.type === 'file') {
        req.files = req.files || {};
        req.files[parameter.name] = answer;
      } else if (parameter.in === 'path') {
        req.path = req.path.replace('{' + parameter.name + '}', answer);
      } else if (parameter.in === 'header') {
        req.headers = req.headers || {};
        req.headers[parameter.name] = answer;
      } else if (parameter.in === 'formData') {
        if (req.body) req.body += '&';
        else req.body = '';
        req.body += parameter.name + '=' + encodeURIComponent(answer);
        contentType = 'application/x-www-form-urlencoded';
      } else if (parameter.in === 'query') {
        req.query = req.query || {};
        req.query[parameter.name] = answer;
        if (parameter.collectionFormat === 'multi') useTraditional = true;
      } else if (parameter.in === 'body') {
        contentType = parameter.contentType || consumes[0];
        if (req.body) {
          if (typeof answer === 'string' && typeof req.body === 'object') {
            answer = JSON.parse(answer);
          }
          req.body = Object.assign({}, req.body, answer);
        } else {
          req.body = contentType === 'application/json' ? JSON.parse(answer) : answer;
        }
      }
    }
    let extendBody = {};
    if (this.parsed.securityDefinitions && op.security) {
      var addedOauth = false;
      op.security.forEach(secReq => {
        for (var secName in secReq) {
          let sec = this.parsed.securityDefinitions[secName];
          if (sec.type === 'apiKey') {
            if (this.secrets.secrets[sec.name]) {
              if (sec.in === 'body') {
                extendBody[sec.name] = this.secrets.secrets[sec.name];
              } else {
                addParam(sec, this.secrets.secrets[sec.name]);
              }
            }
          } else if (sec.type === 'oauth2' && this.secrets.secrets.oauth2 && !addedOauth) {
            if (OAUTH_TOKEN_NAMES[this.parsed.host]) {
              addParam({in: 'query', name: OAUTH_TOKEN_NAMES[this.parsed.host]}, this.secrets.secrets.oauth2);
            } else if (sec.flow === 'implicit') {
              addParam({in: 'query', name: 'access_token'}, this.secrets.secrets.oauth2);
            } else {
              addParam({in: 'header', name: 'Authorization'}, 'Bearer ' + this.secrets.secrets.oauth2)
            }
            addedOauth = true;
          } else if (sec.type === 'basic' && this.secrets.secrets.username && this.secrets.secrets.password) {
            addParam({in: 'header', name: 'Authorization'}, 'Basic ' + btoa(this.secrets.secrets.username + ':' + this.secrets.secrets.password));
          }
        }
      });
    }
    op.parameters.forEach((parameter) => {
      var answer = answers[parameter.name];
      if (parameter.type === 'array') {
        if (parameter.items && parameter.items.type === 'object') {
          let rxstr = parameter.name.replace(/\[/g, '\\[').replace(/\]/g, '\\]') + '\\[\\d+\\].*';
          let rx = new RegExp(rxstr);
          for (let key in answers) {
            if (rx.test(key)) {
              addParam({
                in: parameter.in,
                name: key,
              }, answers[key])
            }
          }
          return;
        } else {
          answer = answer || [];
          if (!answer.length) return;
          var sep = getSeparatorFromFormat(parameter.collectionFormat);
          if (sep) answer = answer.join(sep);
        }
      }
      if (answer === undefined || answer === '') {
        if (parameter.in === 'path') {
          answer = '';
        } else {
          return;
        }
      }
      addParam(parameter, answer);
    });
    for (let key in extendBody) {
      req.body = req.body || {};
      req.body[key] = req.body[key] || extendBody[key];
    }
    if (contentType) {
      req.headers = req.headers || {};
      req.headers['Content-Type'] = contentType;
    }
    if (useTraditional) {
      req.path += '?' + window.jQuery.param(req.query, true);
      delete req.query;
    }
    return req;
  }

  request(opts) {
    var req = this.openAPIRequest(opts);
    var ajaxReq:any = {
      url: req.protocol + '://' + req.domain + req.path,
      method: req.method.toUpperCase(),
      headers: req.headers,
      data: typeof req.body === 'object' ? JSON.stringify(req.body) : req.body,
    }
    if (opts.proxy) {
      ajaxReq.url = opts.proxy + '/proxy/' + req.protocol + '/' + req.domain + req.path;
    }
    if (req.query) {
      ajaxReq.url += '?';
      Object.keys(req.query).forEach((k, idx) => {
        if (idx !== 0) ajaxReq.url += '&';
        ajaxReq.url += k + '=' + encodeURIComponent(req.query[k]);
      })
    }
    if (req.files) {
      ajaxReq.data = new FormData();
      for (var pname in req.files) ajaxReq.data.append(pname, req.files[pname]);
      ajaxReq.cache = ajaxReq.contentType = ajaxReq.processData = false;
    }
    if (window.config.proxy) {
      let proxyURL = typeof window.config.proxy === 'string' ? window.config.proxy : 'https://crossorigin.me/';
      ajaxReq.url = proxyURL + ajaxReq.url;
    }
    return window.jQuery.ajax(ajaxReq);
  }

  resolveReference(ref, cache={}, base=null) {
    if (cache[ref]) return cache[ref];
    var keys = ref.split('/');
    keys.shift();
    var cur = base || this.parsed;
    keys.forEach(k => cur = cur[k]);
    return cache[ref] = cur;
  }

  dereference(obj, cache={}, base=null, seen=[]) {
    if (typeof obj !== 'object' || obj === null) return obj;
    base = base || obj;
    if (seen.indexOf(obj) === -1) {
      seen.push(obj);
    } else {
      return obj;
    }
    if (obj.$ref) return this.resolveReference(obj.$ref, cache, base);
    if (Array.isArray(obj)) {
      for (var i = 0; i < obj.length; ++i) {
        obj[i] = this.dereference(obj[i], cache, base, seen);
      }
      return obj;
    } else if (typeof obj === 'object') {
      for (var key in obj) {
        var val = obj[key];
        obj[key] = this.dereference(val, cache, base, seen);
      }
    }
    return obj;
  }

  replaceXValues(obj) {
    X_VALUES.forEach((v) => {
      if (obj[v] === undefined && obj['x-' + v] !== undefined) {
        obj[v] = obj['x-' + v];
      }
    })
  }

  initialize() {
    const fixParameter = (param, origParam, op) => {
      if (param.$ref) param = JSON.parse(JSON.stringify(this.resolveReference(param.$ref)));
      if (origParam.$ref) origParam = this.resolveReference(origParam.$ref, {}, this.original);
      if (param.in === 'body') {
        param.consumes = op.consumes || ['application/json'];
        if (param.schema) {
          let derefedSchema = param.schema;
          param.schema = origParam.schema;
          param.schema.definitions = this.original.definitions;
          param.schema.title = derefedSchema.title;
        }
      }
      if (param.type === 'array') param.items = param.items || {};
      this.replaceXValues(param);
      return param;
    }

    const fixOperation = (op, origOp, pathParams, origPathParams) => {
      op.responses = op.responses || {};
      op.consumes = op.consumes || this.parsed.consumes;
      op.produces = op.produces || this.parsed.produces;
      op.security = op.security || this.parsed.security;
      op.parameters = (op.parameters || []).concat(pathParams || []);
      let origParams = (origOp.parameters || []).concat(origPathParams || []);
      op.parameters = op.parameters.map((p, idx) => fixParameter(p, origParams[idx], op));
      if (op.produces && op.produces.length > 1) {
        let curAcceptParam = op.parameters.filter(p => p.name.toLowerCase() === 'accept')[0];
        if (!curAcceptParam) {
          let acceptParam = {
            in: 'header',
            name: 'Accept',
            type: 'string',
            enum: op.produces,
            consoleDefault: op.produces[0],
            global: true,
          }
          op.parameters.unshift(acceptParam);
        }
      }
      this.replaceXValues(op);
    }

    this.parsed = window.jQuery.extend(true, {}, this.original);
    this.parsed.host = this.parsed.host || 'localhost';
    this.parsed.schemes = this.parsed.schemes || ['http'];
    this.parsed.definitions = this.parsed.definitions || {};
    this.parsed = this.dereference(this.parsed);
    this.parsed.basePath = this.parsed.basePath || '/';
    for (let key in this.parsed.definitions) {
      let schema = this.parsed.definitions[key];
      let origSchema = this.original.definitions[key];
      schema.title = origSchema.title = schema.title || key;
    }

    if (this.parsed.securityDefinitions) {
      let someOpHasSec = false;
      for (var path in this.parsed.paths) {
        for (var method in this.parsed.paths[path]) {
          if (METHODS.indexOf(method.toUpperCase()) === -1) continue;
          let op = this.parsed.paths[path][method];
          if (op.security) someOpHasSec = true;
        }
      }
      if (!someOpHasSec) {
        this.parsed.security = Object.keys(this.parsed.securityDefinitions).map(k => {
          let ret = {};
          ret[k] = [];
          return ret;
        });
      }
    }

    for (var path in this.parsed.paths) {
      var pathParams = this.parsed.paths[path].parameters || [];
      delete this.parsed.paths[path].parameters;
      for (var method in this.parsed.paths[path]) {
        if (METHODS.indexOf(method.toUpperCase()) === -1) continue;
        fixOperation(this.parsed.paths[path][method], this.original.paths[path][method], pathParams, this.original.paths[path].parameters || [])
      }
    }

    this.secrets.hasSecurity = (this.parsed.securityDefinitions &&
          !!Object.keys(this.parsed.securityDefinitions).length);
    this.apiCalls = [];
    for (let path in this.parsed.paths) {
      for (let method in this.parsed.paths[path]) {
        if (METHODS.indexOf(method.toUpperCase()) === -1) continue;
        let operation = this.parsed.paths[path][method];
        let id = operation.operationId || method.toUpperCase() + ' ' + path;
        let apiCall:APICall = {
          id,
          path,
          method,
          operation,
          title: id,
        }
        this.apiCalls.push(apiCall);
      }
    }
  }

  startOAuth(securityDefinition, scopes) {
    return this.secrets.startOAuth(this.parsed.host, (this.currentVersion || {}).client_id, securityDefinition, scopes);
  }

  fillSchema(smaller, schema, checked=[], recursive=true) {
    if (schema.$ref) schema = this.resolveReference(schema.$ref);
    if (!smaller.properties) return;
    if (checked.indexOf(schema) !== -1) return
    checked.push(schema);
    if (recursive) {
      let subs = [];
      [schema.anyOf, schema.oneOf].filter(s => s)
          .map(s => s.$ref ? this.resolveReference(s.$ref) : s)
          .filter(s => checked.indexOf(s) === -1)
          .forEach(s => subs = subs.concat(s));
      for (let sub of subs) {
        this.fillSchema(smaller, sub, checked, true);
        checked.push(sub);
      }
    }
    let subs = [];
    [schema.allOf].filter(s => s)
        .map(s => s.$ref ? this.resolveReference(s.$ref) : s)
        .filter(s => checked.indexOf(s) === -1)
        .forEach(s => subs = subs.concat(s));
    for (let sub of subs) {
      this.fillSchema(smaller, sub, checked, false);
      checked.push(sub);
    }
    for (let key in smaller.properties) {
      let subschema = schema.properties[key];
      if (!subschema) continue;
      if (subschema.$ref) subschema = this.resolveReference(subschema.$ref);
      smaller.properties[key] = Object.assign({}, subschema, smaller.properties[key], {
          $ref: undefined,
          allOf: undefined,
          anyOf: undefined,
          oneOf: undefined,
          'x-abstract': undefined,
      });
      this.fillSchema(smaller.properties[key], subschema);
    }
  }

  findProperty(schema, key, checked=[]) {
    if (schema.$ref) schema = this.resolveReference(schema.$ref);
    if (schema.properties && schema.properties[key]) return schema.properties[key];
    if (checked.indexOf(schema) !== -1) return
    checked.push(schema);
    let subs = [];
    [schema.allOf, schema.anyOf, schema.oneOf].filter(s => s)
        .map(s => s.$ref ? this.resolveReference(s.$ref) : s)
        .filter(s => checked.indexOf(s) === -1)
        .forEach(s => subs = subs.concat(s));
    checked = checked.concat(subs);
    for (let sub of subs) {
      let prop = this.findProperty(sub, key, checked);
      if (prop) return prop;
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/services/openapi.ts